import Image from 'components/Common/Image/Image'
import TextButton from 'LuxKit/Button/TextButton'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineWhatsappIcon from 'LuxKit/Icons/line/LineWhatsappIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import GeoContext from 'contexts/geoContext'
import { useResponsive } from 'hooks/useResponsive'
import { useRenderWhatsApp } from 'hooks/useRenderWhatsApp'
import { rem } from 'polished'
import { useContext, useMemo } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import ContactContext from 'contexts/contactContext'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import getCustomerSupportAgentImage from '../CustomerSupport/getCustomerSupportAgentImage'
import { useIntl } from 'react-intl'

const SupportContainer = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  display: grid;
  grid-template-areas:
    "mainBody image"
    "mainBody image"
    "buttons buttons";
  padding: ${rem(28)} ${rem(24)};

  ${mediaQueryUp.tablet} {
    grid-template-areas:
      "mainBody image"
      "mainBody image"
      "buttons image";
    padding: ${rem(32)};
  }

  ${mediaQueryUp.desktop} {
    padding: unset;
    justify-content: space-between;
    padding-inline-start: ${rem(32)};
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  grid-area: mainBody;
  grid-gap: ${rem(8)};

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(32)};
  }
`

const ButtonContainer = styled.div`
  grid-area: buttons;
  display: grid;
  grid-gap: ${rem(8)};
  margin-top: ${rem(16)};

  &.two-buttons {
    grid-template-columns: 1fr 1fr;
  }

  ${mediaQueryUp.desktop} {
    padding-bottom: ${rem(32)};
  }
`

const PhoneButton = styled(TextButton)`
  ${mediaQueryUp.tablet} {
    width: fit-content;
  }
`

const SupportImageContainer = styled.div`
  grid-area: image;
  display: flex;
`

const SupportImage = styled(Image)`
  min-width: ${rem(100)};
  min-height: ${rem(120)};
  max-height: ${rem(180)};
  max-width:${rem(150)};
  padding-inline-start: ${rem(20)};

  ${mediaQueryUp.desktop} {
    min-width: unset;
    min-height: unset;
    max-height: unset;
    max-width: ${rem(360)};
  }
`

interface Props {
  title?: string
  description?: string
}

function SupportTile(props: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const supportContact = useContext(ContactContext)
  const [match] = useResponsive()
  const isXXS = match.mobile
  const isXS = match.mobile
  const shouldRenderWhatsApp = useRenderWhatsApp(currentRegionCode)
  const renderWhatsApp = shouldRenderWhatsApp && isXXS
  const isMobileSize = !!(isXS || isXXS)
  const image = getCustomerSupportAgentImage('right', !isMobileSize, currentRegionCode)
  const supportImageWidth = isMobileSize ? 150 : 360

  const is24_7Support = !NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode)

  const defaultTitle = useMemo(() => {
    if (is24_7Support) {
      return intl.formatMessage({
        id: 'vEAOcNlURoGX5723HTP_',
        defaultMessage: 'Our team are available 24 hours, 7 days',
      })
    }
    return intl.formatMessage({
      id: 'TjY4HBv2r32w-Dvc9seM',
      defaultMessage: 'Our team are available 7 days a week from 9am to 7pm',
    })
  }, [intl, is24_7Support])

  const defaultDescription = useMemo(() => {
    return intl.formatMessage({
      id: 'TtyksGR_O1cu9696xQQo',
      defaultMessage: 'Call our dedicated customer service team anytime, whenever you need us.',
    })
  }, [intl])

  const callUsText = useMemo(() => {
    if (isXXS) {
      return intl.formatMessage({
        id: 'mAHB5GUsZ_KUgblg85lP',
        defaultMessage: 'Call us',
      })
    }
    return intl.formatMessage(
      {
        id: '7ngSfLk1TyVbeEWyksYI',
        defaultMessage: 'Call {phoneNumber}',
      },
      { phoneNumber: supportContact.local.humanReadable },
    )
  }, [intl, isXXS, supportContact.local.humanReadable])

  const whatsAppText = useMemo(() => {
    return intl.formatMessage({
      id: 'R0ew3AxBZ4cbO0r1gd-e',
      defaultMessage: 'WhatsApp',
    })
  }, [intl])

  const supportImageAlt = useMemo(() => {
    return intl.formatMessage({
      id: 'H7kIQm-1Fpb09PfeCevh',
      defaultMessage: 'Support',
    })
  }, [intl])

  const {
    title = defaultTitle,
    description = defaultDescription,
  } = props

  return (
    <SupportContainer>
      <ContentContainer>
        <Heading variant="heading5">{title}</Heading>
        <BodyText variant="large">{description}</BodyText>
      </ContentContainer>
      <ButtonContainer className={clsx({ 'two-buttons': renderWhatsApp })}>
        <PhoneButton
          kind="secondary"
          size="medium"
          shape="square"
          startIcon={<LinePhoneIcon />}
          href={`tel:${supportContact.local.number}`}
        >
          {callUsText}
        </PhoneButton>
        {renderWhatsApp &&
          <TextButton
            kind="secondary"
            size="medium"
            shape="square"
            startIcon={<LineWhatsappIcon />}
            href={`https://wa.me/${config.WHATS_APP_NUMBER}`}
          >
            {whatsAppText}
          </TextButton>}
      </ButtonContainer>
      <SupportImageContainer>
        <SupportImage
          fit="center"
          image={image}
          alt={supportImageAlt}
          width={supportImageWidth}
        />
      </SupportImageContainer>
    </SupportContainer>
  )
}

export default SupportTile
